// Component for editing a single curve point
// Now supports OIS helpers, quote references, curve dependencies, and IndexPicker
import { useState, useEffect } from 'react';
import {
  CurvePoint, PointType, QuoteSpec, Curve, IndexDef, IndexRef,
  DEFAULT_DEPOSIT_HELPER, DEFAULT_SWAP_HELPER, DEFAULT_FRA_HELPER,
  DEFAULT_FUTURE_HELPER, DEFAULT_BOND_HELPER, DEFAULT_OIS_HELPER, DEFAULT_DATED_OIS_HELPER,
  DAY_COUNTERS, BUSINESS_DAY_CONVENTIONS, CALENDARS, TIME_UNITS,
  FREQUENCIES,
} from '../../lib/types';
import { getSavedQuotes } from '../../lib/storage/quotes';
import IndexPicker from './IndexPicker';

interface CurvePointEditorProps {
  point?: CurvePoint;
  onSave: (point: CurvePoint) => void;
  onCancel: () => void;
  // For dependency dropdowns (other curves in same set)
  availableCurves?: Curve[];
  currentCurveId?: string;
}

const POINT_TYPES: { type: PointType; label: string }[] = [
  { type: 'DepositHelper', label: 'Deposit' },
  { type: 'OISHelper', label: 'OIS' },
  { type: 'SwapHelper', label: 'Swap' },
  { type: 'FRAHelper', label: 'FRA' },
  { type: 'FutureHelper', label: 'Future' },
  { type: 'BondHelper', label: 'Bond' },
  { type: 'DatedOISHelper', label: 'Dated OIS' },
];

function getDefault(type: PointType): CurvePoint {
  const m: Record<PointType, CurvePoint> = {
    DepositHelper: JSON.parse(JSON.stringify(DEFAULT_DEPOSIT_HELPER)),
    SwapHelper: JSON.parse(JSON.stringify(DEFAULT_SWAP_HELPER)),
    FRAHelper: JSON.parse(JSON.stringify(DEFAULT_FRA_HELPER)),
    FutureHelper: JSON.parse(JSON.stringify(DEFAULT_FUTURE_HELPER)),
    BondHelper: JSON.parse(JSON.stringify(DEFAULT_BOND_HELPER)),
    OISHelper: JSON.parse(JSON.stringify(DEFAULT_OIS_HELPER)),
    DatedOISHelper: JSON.parse(JSON.stringify(DEFAULT_DATED_OIS_HELPER)),
  };
  return m[type];
}

export default function CurvePointEditor({ point, onSave, onCancel, availableCurves, currentCurveId }: CurvePointEditorProps) {
  const [pointType, setPointType] = useState<PointType>(point?.point_type || 'DepositHelper');
  const [currentPoint, setCurrentPoint] = useState<CurvePoint>(point ? JSON.parse(JSON.stringify(point)) : getDefault('DepositHelper'));
  const [quotes, setQuotes] = useState<QuoteSpec[]>([]);
  const [useQuoteRef, setUseQuoteRef] = useState(!!(point?.point as any)?.quote_id);

  useEffect(() => {
    setQuotes(getSavedQuotes());
  }, []);

  const handleTypeChange = (newType: PointType) => {
    setPointType(newType);
    setCurrentPoint(getDefault(newType));
    setUseQuoteRef(false);
  };

  const updateField = (field: string, value: unknown) => {
    setCurrentPoint(prev => ({
      ...prev,
      point: { ...prev.point, [field]: value },
    } as CurvePoint));
  };

  const handleSave = () => onSave(currentPoint);

  const p = currentPoint.point as any;
  const inputClass = "w-full px-3 py-2 bg-white border border-[#d4d4d4] rounded-lg text-sm text-[#0a0a0a] focus:outline-none focus:border-[#8a6a2f] transition-colors";
  const labelClass = "block text-xs text-[#737373] mb-1 font-medium";

  // Rate / Quote selector
  const RateOrQuote = () => (
    <div className="mb-4">
      <div className="flex items-center gap-3 mb-2">
        <label className="flex items-center gap-1.5 text-xs text-[#525252] cursor-pointer">
          <input type="radio" checked={!useQuoteRef} onChange={() => { setUseQuoteRef(false); updateField('quote_id', undefined); }}
            className="text-[#8a6a2f]" />
          Inline rate
        </label>
        <label className="flex items-center gap-1.5 text-xs text-[#525252] cursor-pointer">
          <input type="radio" checked={useQuoteRef} onChange={() => setUseQuoteRef(true)}
            className="text-[#8a6a2f]" />
          Quote reference
        </label>
      </div>
      {useQuoteRef ? (
        <div>
          <label className={labelClass}>Quote ID</label>
          <select value={p.quote_id || ''} onChange={e => { updateField('quote_id', e.target.value || undefined); updateField('rate', undefined); }} className={inputClass}>
            <option value="">— Select quote —</option>
            {quotes.map(q => (
              <option key={q.id} value={q.id}>
                {q.id} — {q.kind === 'Rate' || q.kind === 'Spread' ? `${(q.value * 100).toFixed(3)}%` : q.value.toFixed(2)}
                {q.label ? ` (${q.label})` : ''}
              </option>
            ))}
          </select>
          {quotes.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">No quotes defined. Add quotes in Market Data first.</p>
          )}
        </div>
      ) : (
        <div>
          <label className={labelClass}>
            {pointType === 'FutureHelper' ? 'Price' : 'Rate (%)'}
          </label>
          <input
            type="number"
            step="0.001"
            value={pointType === 'FutureHelper' ? (p.rate ?? '') : ((p.rate ?? 0) * 100).toFixed(3)}
            onChange={e => {
              const v = parseFloat(e.target.value);
              updateField('rate', pointType === 'FutureHelper' ? v : v / 100);
              updateField('quote_id', undefined);
            }}
            className={inputClass}
          />
        </div>
      )}
    </div>
  );

  // Dependency selector for SwapHelper
  const DepsSelector = () => {
    if (!availableCurves || availableCurves.length === 0) return null;
    const otherCurves = availableCurves.filter(c => c.id !== currentCurveId);
    if (otherCurves.length === 0) return null;
    return (
      <div className="col-span-2 pt-3 border-t border-[#e5e5e5]">
        <p className="text-xs font-medium text-[#737373] mb-2">Dependencies</p>
        <div>
          <label className={labelClass}>Discount Curve</label>
          <select
            value={p.deps?.discount_curve?.id || ''}
            onChange={e => updateField('deps', e.target.value ? { discount_curve: { id: e.target.value } } : undefined)}
            className={inputClass}
          >
            <option value="">— Same as bootstrapped —</option>
            {otherCurves.map(c => <option key={c.id} value={c.id}>{c.name} ({c.role})</option>)}
          </select>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-[#f5f5f5] rounded-xl p-5 border border-[#e5e5e5]">
      <h3 className="text-sm font-semibold text-[#0a0a0a] mb-4">
        {point ? 'Edit Instrument' : 'Add Instrument'}
      </h3>

      {/* Type selector */}
      <div className="mb-4">
        <label className={labelClass}>Instrument Type</label>
        <div className="flex flex-wrap gap-1.5">
          {POINT_TYPES.map(({ type, label }) => (
            <button key={type} onClick={() => handleTypeChange(type)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                pointType === type ? 'bg-[#0a0a0a] text-white' : 'bg-white text-[#525252] border border-[#d4d4d4] hover:bg-[#e5e5e5]'
              }`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <RateOrQuote />

      {/* Type-specific fields */}
      {pointType === 'DepositHelper' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Tenor</label>
            <div className="flex gap-2">
              <input type="number" value={p.tenor_number} onChange={e => updateField('tenor_number', parseInt(e.target.value) || 1)} className={`${inputClass} w-20`} />
              <select value={p.tenor_time_unit} onChange={e => updateField('tenor_time_unit', e.target.value)} className={inputClass}>
                {TIME_UNITS.map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelClass}>Fixing Days</label>
            <input type="number" value={p.fixing_days} onChange={e => updateField('fixing_days', parseInt(e.target.value) || 0)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Calendar</label>
            <select value={p.calendar} onChange={e => updateField('calendar', e.target.value)} className={inputClass}>
              {CALENDARS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Day Counter</label>
            <select value={p.day_counter} onChange={e => updateField('day_counter', e.target.value)} className={inputClass}>
              {DAY_COUNTERS.map(dc => <option key={dc}>{dc}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className={labelClass}>Convention</label>
            <select value={p.business_day_convention} onChange={e => updateField('business_day_convention', e.target.value)} className={inputClass}>
              {BUSINESS_DAY_CONVENTIONS.map(b => <option key={b}>{b}</option>)}
            </select>
          </div>
        </div>
      )}

      {pointType === 'OISHelper' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Tenor</label>
            <div className="flex gap-2">
              <input type="number" value={p.tenor_number} onChange={e => updateField('tenor_number', parseInt(e.target.value) || 1)} className={`${inputClass} w-20`} />
              <select value={p.tenor_time_unit} onChange={e => updateField('tenor_time_unit', e.target.value)} className={inputClass}>
                {TIME_UNITS.map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div className="col-span-2">
            <IndexPicker
              label="Overnight Index"
              value={p.overnight_index}
              onChange={(ref: IndexRef, _def: IndexDef) => {
                updateField('overnight_index', ref);
              }}
              filter="Overnight"
            />
          </div>
          <div>
            <label className={labelClass}>Settlement Days</label>
            <input type="number" value={p.settlement_days ?? 2} onChange={e => updateField('settlement_days', parseInt(e.target.value))} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Calendar</label>
            <select value={p.calendar || 'TARGET'} onChange={e => updateField('calendar', e.target.value)} className={inputClass}>
              {CALENDARS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Fixed Leg Frequency</label>
            <select value={p.fixed_leg_frequency || 'Annual'} onChange={e => updateField('fixed_leg_frequency', e.target.value)} className={inputClass}>
              {FREQUENCIES.map(f => <option key={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Fixed Leg Day Counter</label>
            <select value={p.fixed_leg_day_counter || 'Actual360'} onChange={e => updateField('fixed_leg_day_counter', e.target.value)} className={inputClass}>
              {DAY_COUNTERS.map(dc => <option key={dc}>{dc}</option>)}
            </select>
          </div>
          <DepsSelector />
        </div>
      )}

      {pointType === 'DatedOISHelper' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Start Date</label>
            <input type="date" value={p.start_date} onChange={e => updateField('start_date', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>End Date</label>
            <input type="date" value={p.end_date} onChange={e => updateField('end_date', e.target.value)} className={inputClass} />
          </div>
          <div className="col-span-2">
            <IndexPicker
              label="Overnight Index"
              value={p.overnight_index}
              onChange={(ref: IndexRef, _def: IndexDef) => {
                updateField('overnight_index', ref);
              }}
              filter="Overnight"
            />
          </div>
          <div>
            <label className={labelClass}>Calendar</label>
            <select value={p.calendar || 'TARGET'} onChange={e => updateField('calendar', e.target.value)} className={inputClass}>
              {CALENDARS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Fixed Leg Day Counter</label>
            <select value={p.fixed_leg_day_counter || 'Actual360'} onChange={e => updateField('fixed_leg_day_counter', e.target.value)} className={inputClass}>
              {DAY_COUNTERS.map(dc => <option key={dc}>{dc}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Settlement Days</label>
            <input type="number" value={p.settlement_days ?? 2} onChange={e => updateField('settlement_days', parseInt(e.target.value))} className={inputClass} />
          </div>
        </div>
      )}

      {pointType === 'SwapHelper' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Tenor</label>
            <div className="flex gap-2">
              <input type="number" value={p.tenor_number} onChange={e => updateField('tenor_number', parseInt(e.target.value) || 1)} className={`${inputClass} w-20`} />
              <select value={p.tenor_time_unit} onChange={e => updateField('tenor_time_unit', e.target.value)} className={inputClass}>
                {TIME_UNITS.map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div className="col-span-2">
            <IndexPicker
              label="Floating Index"
              value={p.float_index}
              onChange={(ref: IndexRef, _def: IndexDef) => {
                updateField('float_index', ref);
              }}
              filter="Ibor"
            />
          </div>
          <div>
            <label className={labelClass}>Fixed Leg Frequency</label>
            <select value={p.sw_fixed_leg_frequency} onChange={e => updateField('sw_fixed_leg_frequency', e.target.value)} className={inputClass}>
              {FREQUENCIES.map(f => <option key={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Fixed Leg Day Counter</label>
            <select value={p.sw_fixed_leg_day_counter} onChange={e => updateField('sw_fixed_leg_day_counter', e.target.value)} className={inputClass}>
              {DAY_COUNTERS.map(dc => <option key={dc}>{dc}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Calendar</label>
            <select value={p.calendar} onChange={e => updateField('calendar', e.target.value)} className={inputClass}>
              {CALENDARS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Spread</label>
            <input type="number" step="0.0001" value={p.spread ?? 0} onChange={e => updateField('spread', parseFloat(e.target.value) || 0)} className={inputClass} />
          </div>
          <DepsSelector />
        </div>
      )}

      {pointType === 'FRAHelper' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Months to Start</label>
            <input type="number" value={p.months_to_start} onChange={e => updateField('months_to_start', parseInt(e.target.value) || 1)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Months to End</label>
            <input type="number" value={p.months_to_end} onChange={e => updateField('months_to_end', parseInt(e.target.value) || 3)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Fixing Days</label>
            <input type="number" value={p.fixing_days} onChange={e => updateField('fixing_days', parseInt(e.target.value) || 0)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Calendar</label>
            <select value={p.calendar} onChange={e => updateField('calendar', e.target.value)} className={inputClass}>
              {CALENDARS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Day Counter</label>
            <select value={p.day_counter} onChange={e => updateField('day_counter', e.target.value)} className={inputClass}>
              {DAY_COUNTERS.map(dc => <option key={dc}>{dc}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Convention</label>
            <select value={p.business_day_convention} onChange={e => updateField('business_day_convention', e.target.value)} className={inputClass}>
              {BUSINESS_DAY_CONVENTIONS.map(b => <option key={b}>{b}</option>)}
            </select>
          </div>
        </div>
      )}

      {pointType === 'FutureHelper' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Future Start Date</label>
            <input type="date" value={p.future_start_date} onChange={e => updateField('future_start_date', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Future Months</label>
            <input type="number" value={p.future_months} onChange={e => updateField('future_months', parseInt(e.target.value) || 3)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Calendar</label>
            <select value={p.calendar} onChange={e => updateField('calendar', e.target.value)} className={inputClass}>
              {CALENDARS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Day Counter</label>
            <select value={p.day_counter} onChange={e => updateField('day_counter', e.target.value)} className={inputClass}>
              {DAY_COUNTERS.map(dc => <option key={dc}>{dc}</option>)}
            </select>
          </div>
        </div>
      )}

      {pointType === 'BondHelper' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Settlement Days</label>
              <input type="number" value={p.settlement_days} onChange={e => updateField('settlement_days', parseInt(e.target.value) || 2)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Face Amount</label>
              <input type="number" value={p.face_amount} onChange={e => updateField('face_amount', parseFloat(e.target.value) || 100)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Coupon Rate (%)</label>
              <input type="number" step="0.01" value={((p.coupon_rate ?? 0) * 100).toFixed(2)}
                onChange={e => updateField('coupon_rate', (parseFloat(e.target.value) || 0) / 100)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Redemption</label>
              <input type="number" value={p.redemption} onChange={e => updateField('redemption', parseFloat(e.target.value) || 100)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Issue Date</label>
              <input type="date" value={p.issue_date} onChange={e => updateField('issue_date', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Day Counter</label>
              <select value={p.day_counter} onChange={e => updateField('day_counter', e.target.value)} className={inputClass}>
                {DAY_COUNTERS.map(dc => <option key={dc}>{dc}</option>)}
              </select>
            </div>
          </div>
          <div className="pt-3 border-t border-[#e5e5e5]">
            <p className="text-xs font-medium text-[#737373] mb-2">Schedule</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Effective Date</label>
                <input type="date" value={p.schedule?.effective_date || ''} onChange={e => updateField('schedule', { ...p.schedule, effective_date: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Termination Date</label>
                <input type="date" value={p.schedule?.termination_date || ''} onChange={e => updateField('schedule', { ...p.schedule, termination_date: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Frequency</label>
                <select value={p.schedule?.frequency || 'Annual'} onChange={e => updateField('schedule', { ...p.schedule, frequency: e.target.value })} className={inputClass}>
                  {FREQUENCIES.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Calendar</label>
                <select value={p.schedule?.calendar || 'TARGET'} onChange={e => updateField('schedule', { ...p.schedule, calendar: e.target.value })} className={inputClass}>
                  {CALENDARS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 mt-5">
        <button onClick={onCancel} className="flex-1 px-4 py-2 text-sm font-medium text-[#525252] bg-white border border-[#d4d4d4] rounded-lg hover:bg-[#f5f5f5]">
          Cancel
        </button>
        <button onClick={handleSave} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#0a0a0a] rounded-lg hover:bg-[#262626]">
          {point ? 'Update' : 'Add Instrument'}
        </button>
      </div>
    </div>
  );
}
